import type { AxiosInstance, AxiosResponse } from 'axios';
import { apiClient } from './api-client';

export const PROVIDERS = [
  'SMTP',
  'MAILTRAP',
  'BREVO',
  'SES',
  'MSG91',
  'SENDGRID',
  'CUSTOM',
] as const;
export const GATEWAY_TYPES = ['SMTP', 'API'] as const;
export const SMTP_ENCRYPTIONS = ['NONE', 'TLS', 'SSL', 'STARTTLS'] as const;

export type Provider = (typeof PROVIDERS)[number];
export type GatewayType = (typeof GATEWAY_TYPES)[number];
export type SmtpEncryption = (typeof SMTP_ENCRYPTIONS)[number];
export type HealthStatus = 'HEALTHY' | 'WARNING' | 'ERROR' | 'UNKNOWN';

export interface EmailGateway {
  id: string;
  tenantId: string;
  name: string;
  provider: Provider;
  gatewayType: GatewayType;
  description: string | null;
  // SMTP
  smtpHost: string | null;
  smtpPort: number | null;
  smtpUsername: string | null;
  hasSmtpPassword: boolean;
  smtpEncryption: SmtpEncryption | null;
  // API
  apiEndpoint: string | null;
  hasApiKey: boolean;
  hasApiSecretKey: boolean;
  senderName: string | null;
  senderEmail: string | null;
  replyToEmail: string | null;
  // Config
  status: 'ENABLED' | 'DISABLED';
  isDefault: boolean;
  /** Platform gateways only — the Platform Backup (resolution step 5). */
  isBackup?: boolean;
  priority: number;
  fallbackEnabled: boolean;
  // Health
  healthStatus: HealthStatus;
  lastTestedAt: string | null;
  lastSuccessfulTestAt: string | null;
  lastFailedTestAt: string | null;
  lastUsedAt: string | null;
  // Audit
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEmailGatewayPayload {
  name: string;
  provider: Provider;
  gatewayType: GatewayType;
  description?: string;
  smtpHost?: string;
  smtpPort?: number;
  smtpUsername?: string;
  smtpPassword?: string;
  smtpEncryption?: SmtpEncryption;
  apiEndpoint?: string;
  apiKey?: string;
  apiSecretKey?: string;
  senderName?: string;
  senderEmail?: string;
  replyToEmail?: string;
  status?: 'ENABLED' | 'DISABLED';
  isDefault?: boolean;
  /** Platform gateways only. */
  isBackup?: boolean;
  priority?: number;
  fallbackEnabled?: boolean;
}

export type UpdateEmailGatewayPayload = Partial<CreateEmailGatewayPayload>;

export interface TestEmailPayload {
  recipientEmail: string;
  subject?: string;
  message?: string;
}

export interface TestEmailResult {
  success: boolean;
  message: string;
  testType: 'FULL_DELIVERY' | 'CONNECTIVITY';
  connectionStatus: 'CONNECTED' | 'FAILED' | 'TIMEOUT';
  authStatus: 'AUTHENTICATED' | 'FAILED' | 'NOT_TESTED';
  /**
   * Whether a real message was handed to the provider. ACCEPTED is not a claim
   * that it reached a mailbox — that is asynchronous and not observable here.
   */
  sendStatus: 'ACCEPTED' | 'FAILED' | 'NOT_ATTEMPTED';
  responseTimeMs: number;
  gatewayResponse?: string;
  errorDetail?: string;
  testedAt: string;
}

export interface GatewayListParams {
  search?: string;
  status?: string;
  gatewayType?: string;
}

const BASE = '/email-gateways';

/**
 * Builds a gateway service bound to a given API client and base path.
 *
 * The tenant surface (`apiClient` + `/email-gateways`) and the Platform Console
 * surface (`platformApiClient` + `/platform/email-gateways`) expose identical
 * routes, so both are driven by this one implementation — the Gateway
 * Management screen is shared rather than duplicated.
 *
 * Both clients' response interceptors already unwrap { data, meta } → payload,
 * so `r.data` is the payload. Never double-unwrap.
 */
export function createEmailGatewayService(client: AxiosInstance, base: string) {
  return {
    list: (params: GatewayListParams = {}): Promise<EmailGateway[]> =>
      client
        .get<EmailGateway[]>(base, { params })
        .then((r: AxiosResponse<EmailGateway[]>) => (Array.isArray(r.data) ? r.data : [])),

    get: (id: string): Promise<EmailGateway> =>
      client.get<EmailGateway>(`${base}/${id}`).then((r: AxiosResponse<EmailGateway>) => r.data),

    create: (payload: CreateEmailGatewayPayload): Promise<EmailGateway> =>
      client.post<EmailGateway>(base, payload).then((r: AxiosResponse<EmailGateway>) => r.data),

    update: (id: string, payload: UpdateEmailGatewayPayload): Promise<EmailGateway> =>
      client
        .patch<EmailGateway>(`${base}/${id}`, payload)
        .then((r: AxiosResponse<EmailGateway>) => r.data),

    remove: (id: string): Promise<void> =>
      client.delete(`${base}/${id}`).then((): void => undefined),

    enable: (id: string): Promise<EmailGateway> =>
      client
        .patch<EmailGateway>(`${base}/${id}/enable`)
        .then((r: AxiosResponse<EmailGateway>) => r.data),

    disable: (id: string): Promise<EmailGateway> =>
      client
        .patch<EmailGateway>(`${base}/${id}/disable`)
        .then((r: AxiosResponse<EmailGateway>) => r.data),

    setDefault: (id: string): Promise<EmailGateway> =>
      client
        .patch<EmailGateway>(`${base}/${id}/set-default`)
        .then((r: AxiosResponse<EmailGateway>) => r.data),

    /** Platform-only — marks the Platform Backup gateway (resolution step 5). */
    setBackup: (id: string): Promise<EmailGateway> =>
      client
        .patch<EmailGateway>(`${base}/${id}/set-backup`)
        .then((r: AxiosResponse<EmailGateway>) => r.data),

    test: (id: string, payload: TestEmailPayload): Promise<TestEmailResult> =>
      client
        .post<TestEmailResult>(`${base}/${id}/test`, payload)
        .then((r: AxiosResponse<TestEmailResult>) => r.data),
  };
}

export type EmailGatewayServiceApi = ReturnType<typeof createEmailGatewayService>;

export const emailGatewayService = createEmailGatewayService(apiClient, BASE);
